import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Component to wrap public routes and prevent excessive API calls
 * It will block API calls to protected routes that require authentication
 */
const PublicRouteWrapper = ({ children }) => {
  const location = useLocation();
  
  useEffect(() => {
    // Check if we're on a public route (exclude register to allow real API calls)
    const isPublicRoute = 
      location.pathname === '/landing' ||
      location.pathname === '/login' || 
      location.pathname === '/forgot-password';
    
    if (isPublicRoute) {
      // Create a proxy to intercept fetch and XMLHttpRequest
      const originalFetch = window.fetch;
      const originalXHR = window.XMLHttpRequest.prototype.open;
      const originalSend = window.XMLHttpRequest.prototype.send;
      
      // List of allowed API endpoints on public routes
      const allowedEndpoints = [
        '/api/subscriptions/plans',
        '/api/saas/subscriptions/plans',
        '/api/tenants',
        '/api/saas/tenants',
        '/api/auth/login',
        '/api/auth/register',
        '/api/settings/public'
      ];

      // Mock response for blocked API calls
      const mockResponse = {
        visitors: [],
        villas: [],
        users: [],
        units: [],
        settings: {},
        complaints: [],
        financials: []
      };
      
      // Override fetch to block API calls except for public endpoints
      window.fetch = function(url) {
        // Bypass interception for absolute URLs (backend calls)
        if (typeof url === 'string' && (url.startsWith('http://') || url.startsWith('https://'))) {
          return originalFetch.apply(this, arguments);
        }
        if (typeof url === 'string') {
          // Check if this is an allowed endpoint
          const isAllowed = allowedEndpoints.some(endpoint => url.includes(endpoint)); 
          if (isAllowed) {
            return originalFetch.apply(this, arguments);
          }
          // Block all other API calls to the server
          if (url.includes('/api/')) {
            console.log('Blocked API call in public route:', url);
            return Promise.resolve(new Response(JSON.stringify(mockResponse), {
              status: 200,
              headers: { 'Content-Type': 'application/json' }
            }));
          }
        }
        // Allow other fetch calls to proceed
        return originalFetch.apply(this, arguments);
      };
      
      // Override XMLHttpRequest to block API calls except for public endpoints
      window.XMLHttpRequest.prototype.open = function(method, url, ...args) {
        // Bypass interception for absolute URLs (backend calls)
        if (typeof url === 'string' && (url.startsWith('http://') || url.startsWith('https://'))) {
          return originalXHR.apply(this, [method, url, ...args]);
        }
        this._url = url; // Store the URL for reference
        if (typeof url === 'string' && url.includes('/api/')) {
          // Check if this is an allowed endpoint
          const isAllowed = allowedEndpoints.some(endpoint => url.includes(endpoint));
          if (!isAllowed) {
            console.log('Intercepting XHR call in public route:', url);
            this._blocked = true;
            return originalXHR.apply(this, [method, url, ...args]);
          }
        }
        // Allow this XHR call to proceed normally
        return originalXHR.apply(this, [method, url, ...args]);
      };
      
      // Override the send method to return mock data for blocked requests
      window.XMLHttpRequest.prototype.send = function(...args) {
        if (this._blocked) {
          // Simulate a successful response
          const self = this;
          setTimeout(() => {
            // Set response properties
            Object.defineProperty(self, 'status', { value: 200 });
            Object.defineProperty(self, 'statusText', { value: 'OK' });
            Object.defineProperty(self, 'responseText', { value: JSON.stringify(mockResponse) });
            Object.defineProperty(self, 'response', { value: JSON.stringify(mockResponse) });
            
            // Trigger readystatechange
            self.readyState = 4;
            self.dispatchEvent(new Event('readystatechange'));
            self.dispatchEvent(new Event('load'));
          }, 0);
          
          return;
        }
        
        return originalSend.apply(this, args);
      };
      
      // Restore original functions when component unmounts
      return () => {
        window.fetch = originalFetch;
        window.XMLHttpRequest.prototype.open = originalXHR;
        window.XMLHttpRequest.prototype.send = originalSend;
      };
    }
  }, [location.pathname]);
  
  return <>{children}</>;
};

export default PublicRouteWrapper; 